import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Search,
  ChefHat,
  ClipboardList,
  Package,
  BookOpen,
  Home,
  Printer,
  Scale,
  FileText,
  ArrowRight,
  Command
} from 'lucide-react'
import { recipesAPI, exportAPI } from '../lib/api'

// ══════════════════════════════════════════════════════════════════════
// COMMAND PALETTE
// ══════════════════════════════════════════════════════════════════════

export default function CommandPalette({ isOpen, onClose, onNavigate, recipes = [], currentMenu }) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
      setQuery('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Build commands list
  const commands = useMemo(() => {
    const baseCommands = [
      {
        id: 'nav-dashboard',
        icon: Home,
        title: 'Go to Dashboard',
        subtitle: 'View your command center',
        action: () => { onNavigate('dashboard'); onClose(); }
      },
      {
        id: 'nav-print-materials',
        icon: Printer,
        title: 'Go to Print Materials',
        subtitle: 'Menu card, reheating card, salad card, and stickers for this menu',
        action: () => { onNavigate('print-materials'); onClose(); }
      },
      {
        id: 'nav-menu-builder',
        icon: ChefHat,
        title: 'Go to Menu Builder',
        subtitle: 'Build and customize your weekly menu',
        action: () => { onNavigate('menu-builder'); onClose(); }
      },
      {
        id: 'nav-meal-counts',
        icon: ClipboardList,
        title: 'Go to Meal Counts',
        subtitle: 'Enter production quantities',
        action: () => { onNavigate('meal-counts'); onClose(); }
      },
      {
        id: 'nav-ordering',
        icon: Package,
        title: 'Go to Ordering',
        subtitle: 'View order quantities',
        action: () => { onNavigate('ordering'); onClose(); }
      },
      {
        id: 'nav-recipes',
        icon: BookOpen,
        title: 'Go to Recipes',
        subtitle: 'Browse recipe library',
        action: () => { onNavigate('recipes'); onClose(); }
      },
      {
        id: 'nav-components',
        icon: Scale,
        title: 'Go to Component Library',
        subtitle: 'All proteins, vegetables, carbs, and sauces',
        action: () => { onNavigate('components'); onClose(); }
      },
      {
        id: 'nav-menu-development',
        icon: FileText,
        title: 'Go to Menu Development',
        subtitle: 'Build weekly menus from the dish library',
        action: () => { onNavigate('menu-development'); onClose(); }
      },
      {
        id: 'action-print-order',
        icon: Printer,
        title: 'Print Order Sheet',
        subtitle: 'Open printable order sheet',
        action: () => {
          if (!currentMenu) {
            onClose();
            return;
          }
          exportAPI.openOrderSheet(currentMenu);
          onClose();
        }
      },
      {
        id: 'action-print-prep',
        icon: Printer,
        title: 'Print Prep Sheet',
        subtitle: 'Open prep sheet by station',
        action: () => {
          if (!currentMenu) {
            onClose();
            return;
          }
          exportAPI.openPrepSheet(currentMenu);
          onClose();
        }
      },
    ]

    // Add recipe commands
    const recipeCommands = recipes.slice(0, 20).map(recipe => ({
      id: `recipe-${recipe.id}`,
      icon: ChefHat,
      title: recipe.name,
      subtitle: `${recipe.category} • ${recipe.base_batch_size} ${recipe.base_batch_unit}`,
      action: () => {
        onNavigate('recipes')
        // Could also trigger a modal to scale this specific recipe
        onClose()
      }
    }))

    return [...baseCommands, ...recipeCommands]
  }, [recipes, onNavigate, onClose, currentMenu])

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query) return commands.slice(0, 8)

    const lowerQuery = query.toLowerCase()
    return commands.filter(cmd =>
      cmd.title.toLowerCase().includes(lowerQuery) ||
      cmd.subtitle.toLowerCase().includes(lowerQuery)
    ).slice(0, 10)
  }, [commands, query])

  // Reset selected index when filtered commands change
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredCommands])

  // Keyboard navigation
  function handleKeyDown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action()
        }
        break
      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.children[selectedIndex]
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center pt-[15vh] animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-elevated overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-havn-cream-200">
          <Search className="w-5 h-5 text-havn-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search commands, recipes, or pages..."
            className="flex-1 text-base text-havn-charcoal placeholder:text-havn-gray-400 outline-none bg-transparent"
          />
          <kbd className="px-2 py-1 text-xs text-havn-gray-400 bg-havn-cream-200 rounded">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-havn-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-havn-gray-300" />
              <div>No results found</div>
              <div className="text-sm text-havn-gray-400 mt-1">Try a different search term</div>
            </div>
          ) : (
            filteredCommands.map((cmd, i) => (
              <button
                key={cmd.id}
                onClick={cmd.action}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
                  ${i === selectedIndex ? 'bg-havn-green-700 text-white' : 'text-havn-charcoal hover:bg-havn-cream-100'}
                `}
              >
                <div className={`
                  w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                  ${i === selectedIndex ? 'bg-white/20' : 'bg-havn-cream-200'}
                `}>
                  <cmd.icon className={`w-4 h-4 ${i === selectedIndex ? 'text-white' : 'text-havn-gray-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${i === selectedIndex ? 'text-white' : ''}`}>
                    {cmd.title}
                  </div>
                  <div className={`text-sm truncate ${i === selectedIndex ? 'text-white/70' : 'text-havn-gray-500'}`}>
                    {cmd.subtitle}
                  </div>
                </div>
                <ArrowRight className={`w-4 h-4 ${i === selectedIndex ? 'text-white/70' : 'text-havn-gray-300'}`} />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-havn-cream-50 border-t border-havn-cream-200 flex items-center justify-between text-xs text-havn-gray-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-havn-cream-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-havn-cream-200 rounded">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-havn-cream-200 rounded">↵</kbd>
              to select
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Command className="w-3 h-3" />
            <span>K to open anytime</span>
          </div>
        </div>
      </div>
    </div>
  )
}
